/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { PageWrapper } from "../../interfaceParts/PageWrapper"
import { IRecord } from "../../types/schema"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useSkeem } from "../../providers/SkeemProvider"
import { useEffect, useState } from "react"
import { renderTable } from "../user/renderTable"
import { LoadingFrame } from "../user/UserResultsShowPage/AssessmentDetailsTab"
import { Pill } from "../../interfaceParts/Pill"
import { QuickToolTip } from "../../interfaceParts/QuickTooltip"
import { formatFullDateTime } from "../../../utils/date"
import { icons } from "../../utils/icons"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useReloadKey } from "../../hooks/useReloadKey"
import { useCreateTaskDialog } from "../user/UserManageUserPage/UserManageUserActionsTab"
import { useFetchMe } from "../../hooks/useFetchMe"
import { Frame } from "../../interfaceParts/Frame"
import { Button, Grid, IconButton, Spacer, Spinner } from "@cd2/components"
import { useQuickConfirmationModal } from "../../hooks/useQuickConfirmationModal"
import { useMutatorState } from "../../hooks/useMutatorState"
import { buildSearchFilter } from "../../utils/buildSearchFilter"
import { Card } from "../../interfaceParts/Card"
import { Fields } from "@cd2/forms"
import { ToggleSwitchField } from "../../interfaceParts/ToggleSwitch"
import { Flex } from "../../interfaceParts/Flex"
import { TextWithNewLines } from "../../interfaceParts/TextWithNewLines"
import { doo } from "@cd2/tools"

export function EmployeesTasksPage() {
  type Reminder = IRecord<
    "reminders",
    "body" | "date" | "archived" | "completed" | "completedOn" | "createdAt"
  >

  const skeem = useSkeem()
  const me = useFetchMe()
  const [reminders, setReminders] = useState<Reminder[] | null>(null)
  const [reloadKey, triggerReload] = useReloadKey()
  const [filters, mutate] = useMutatorState({
    showArchived: false,
    showComplete: false,
    search: "",
    stableSearch: "",
  })

  useEffect(() => {
    if (filters.search === filters.stableSearch) return

    const timeout = setTimeout(() => {
      mutate.setProp("stableSearch", filters.search)
    }, 500)

    return () => clearTimeout(timeout)
  }, [filters.search])

  useAsyncEffect(async () => {
    const records = await skeem.fetch2<Reminder[]>({
      model: {
        name: "reminders",
        attributes: ["body", "date", "archived", "completed", "completedOn", "createdAt"],
        sort: { by: "date", direction: "desc" },
        filter: {
          and: [
            {
              matches: {
                user: { session: true },
                archived: filters.showArchived ? undefined : false,
                completed: filters.showComplete ? undefined : false,
              },
            },
            buildSearchFilter(["body"], filters.stableSearch),
          ],
        },
      },
    })
    setReminders(records)
  }, [reloadKey, filters.showArchived, filters.showComplete, filters.stableSearch])

  const [triggerNewTask, newTaskNode] = useCreateTaskDialog()
  const handleNewTask = useAsyncCallback(async () => {
    if (!me) return
    const shouldReload = await triggerNewTask({ preventChangingUser: true, user: { id: me.id } })

    if (shouldReload) triggerReload()
  })

  const [triggerConfirmation, confirmationNode] = useQuickConfirmationModal()
  const handleComplete = useAsyncCallback(async (id: string) => {
    const shouldContinue = await triggerConfirmation({
      heading: "Mark Task as Completed?",
      yesText: "Mark as Completed!",
    })
    if (!shouldContinue) return

    if (!me) return
    await skeem.mutate({ reminders: { update: { id, completed: true, completedOn: new Date() } } })
    triggerReload()
  })

  const handleDelete = useAsyncCallback(async (id: string) => {
    const shouldContinue = await triggerConfirmation({
      heading: "Delete Task?",
      yesText: "Delete!",
    })
    if (!shouldContinue) return

    await skeem.mutate({ reminders: { update: { id, archived: true } } })
    triggerReload()
  })

  return (
    <PageWrapper title="My Tasks">
      <Flex alignItems="end">
        <Button leftIcon={icons.plus} onClick={handleNewTask}>
          New Task
        </Button>
        <Flex.Spacer />

        <Card>
          <Fields.Search
            label="Search"
            value={filters.search}
            onChange={mutate.setter("search")}
            suffix={
              filters.search !== filters.stableSearch ? (
                <Spinner color="black" size={30} />
              ) : undefined
            }
          />
          <Spacer />
          <ToggleSwitchField
            label="Show Archived"
            value={filters.showArchived}
            onChange={mutate.setter("showArchived")}
          />
          <Spacer />
          <ToggleSwitchField
            label="Show Completed"
            value={filters.showComplete}
            onChange={mutate.setter("showComplete")}
          />
        </Card>
      </Flex>
      <Spacer />
      {newTaskNode}
      {confirmationNode}
      {renderTable({
        data: reminders ?? [],
        keyExtractor: row => row.id,
        css: css`
          .archived {
            background-color: #f6ffa2;
          }
          .completed {
            background-color: #abffa2;
          }
        `,
        rowClass: row => (row.archived ? "archived" : row.completed ? "completed" : ""),
        columns: [
          { heading: "Description", render: row => <TextWithNewLines text={row.body} stripHtml /> },
          { heading: "Due Date", render: row => (row.date ? formatFullDateTime(row.date) : "-") },
          {
            heading: "Status",
            render: row => {
              if (row.completed) {
                return (
                  <QuickToolTip
                    content={`Completed on ${
                      row.completedOn ? formatFullDateTime(row.completedOn) : "-"
                    }`}
                  >
                    <Pill color="green">Completed</Pill>
                  </QuickToolTip>
                )
              } else if (row.archived) {
                return <Pill color="gray">Archived</Pill>
              } else {
                return <Pill color="blue">Pending</Pill>
              }
            },
          },
          {
            heading: "Created At",
            render: row => (row.createdAt ? formatFullDateTime(row.createdAt) : "-"),
          },
          {
            render: row => (
              <ButtonGrid>
                <Button leftIcon={icons.check} onClick={() => handleComplete(row.id)}>
                  Complete
                </Button>
                <IconButton
                  icon={icons.delete}
                  onClick={() => handleDelete(row.id)}
                  buttonStyle="text"
                />
              </ButtonGrid>
            ),
          },
        ],
      })}
      {reminders && reminders.length === 0 && (
        <Frame minHeight={200}>
          <i>
            {doo(() => {
              if (!filters.search) {
                return "Up to date!"
              } else {
                return "No results found."
              }
            })}
          </i>
        </Frame>
      )}
      {!reminders && <LoadingFrame />}
    </PageWrapper>
  )
}

export function ButtonGrid({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <Grid
      className={className}
      gap={4}
      columns={{ auto: "max-content" }}
      flow="column"
      alignItems="center"
    >
      {children}
    </Grid>
  )
}
